import {
	SYS_SPACE_FIXED_150,
	SYS_SPACE_FIXED_200,
	SYS_SPACE_FIXED_300,
	SYS_SPACE_FIXED_450,
} from '@oneaudi/unified-web-common';
import { SelectField, Text } from '@oneaudi/unified-web-components';
import { useMemo } from 'react';
import styled from 'styled-components';
import { AccessoriesItem } from '~/graphql/mapper';
import { mobileBreakPoint } from '~/styles/break-points';
import { DATA_TEST_ID } from '~/test/data-test-id';

export type SelectionOption = {
	id: string;
	label: string;
	active: boolean;
	items: AccessoriesItem[];
};

type SelectFieldProps = {
	selectionOptions: SelectionOption[];
	label: string;
	onChange: (selectedOption: SelectionOption) => void;
};

export const CategoriesMobile = ({ selectionOptions, label, onChange }: SelectFieldProps) => {
	const selectedOption = useMemo(() => selectionOptions.find(({ active }) => active), [selectionOptions]);
	return (
		<CategoriesMobileStyle>
			<SelectField
				id={label}
				label={label}
				value={selectedOption?.id || 'none'}
				onChange={(event) => {
					const selectOption = selectionOptions.find(({ id }) => id === event.target.value);
					if (!selectOption) return;
					onChange(selectOption);
				}}
				data-testid={DATA_TEST_ID.SELECTION_ACCESSORIES_CATEGORY_SELECT}>
				{selectionOptions.map(({ id, items, label: optionLabel }) => (
					<option key={id} value={id}>
						{`${optionLabel} (${items.length})`}
					</option>
				))}
			</SelectField>
		</CategoriesMobileStyle>
	);
};

export const CategoriesDesktop = ({ selectionOptions, label, onChange }: SelectFieldProps) => {
	return (
		<CategoriesDesktopStyle>
			<Text variant={{ order: '2', type: 'copy', style: 'normal' }} tag="p">
				{label}
			</Text>
			<div className="options-container">
				{selectionOptions.map((option) => {
					return (
						<button
							type="button"
							className={`selection-option ${option.active ? 'is-selected' : ''}`}
							onClick={() => onChange(option)}
							key={option.id}
							data-testid={DATA_TEST_ID.SELECTION_ACCESSORIES_CATEGORY_BUTTON}>
							<Text variant={{ order: '1', type: 'copy', style: 'normal' }} tag="p">
								{`${option.label} (${option.items.length})`}
							</Text>
						</button>
					);
				})}
			</div>
		</CategoriesDesktopStyle>
	);
};

const CategoriesMobileStyle = styled.div`
	padding-top: ${SYS_SPACE_FIXED_300};
	padding-bottom: ${SYS_SPACE_FIXED_450};
	display: flex;
	position: sticky;
	top: 0;
	z-index: 2;
	grid-column: 1 / -1;
	background-color: white;

	div {
		width: 100%;
		select {
			text-overflow: ellipsis;
			padding-right: ${SYS_SPACE_FIXED_300};
		}
	}

	@media (min-width: ${mobileBreakPoint}px) {
		display: none;
	}
`;

const CategoriesDesktopStyle = styled.div`
	display: none;
	flex-direction: column;
	gap: ${SYS_SPACE_FIXED_200};

	@media (min-width: ${mobileBreakPoint}px) {
		display: flex;
	}

	.options-container {
		display: flex;
		flex-direction: column;
	}

	.selection-option {
		margin-top: ${SYS_SPACE_FIXED_150};
		margin-bottom: ${SYS_SPACE_FIXED_150};
		width: fit-content;
		text-align: left;

		p {
			position: relative;
			color: #666;
			transition: color ease-out 0.1s;
		}

		&.is-selected,
		&:hover {
			p {
				color: #000;
			}
		}
		&.is-selected > p::after {
			transform: scaleX(1);
		}
		p::after {
			top: 2px;
			left: 0;
			position: absolute;
			content: ' ';
			display: block;
			height: 100%;
			width: 100%;
			content: ' ';
			transform: scaleX(0);
			transition: transform ease-out 0.1s;
			border-bottom: 1px solid black;
		}
	}
`;
